import { type CurrencyInfo } from '@src/core/buyerApiClient';
import {
  calculateAmountDiscountCents,
  getCouponDiscountCurrencyInfo,
  getCouponPercentLabel,
  SaleCouponTypeEnum,
} from '@src/domains/coupons';
import type { Sale, SaleCoupon, DetailedSaleItem } from '@src/domains/sales/types/sale';

export const getSaleItems = (sale: Sale | null | undefined): DetailedSaleItem[] => sale?.items ?? [];

export const getSaleTotalCurrencyInfo = (sale: Sale | null | undefined): CurrencyInfo | undefined =>
  sale?.totalAmountCurrencyInfo;

export const getSaleServiceFeeCurrencyInfo = (sale: Sale | null | undefined): CurrencyInfo | undefined =>
  sale?.serviceFeeCurrencyInfo ?? undefined;

export const getSaleCoupon = (sale: Sale | null | undefined): SaleCoupon | null | undefined => sale?.coupon;

export const getSaleProductCount = (sale: Sale | null | undefined): number => getSaleItems(sale).length;

export const getSaleSubtotalCents = (sale: Sale | null | undefined): number =>
  sale?.partialAmountCurrencyInfo.amountCents ?? 0;

export const getSaleTotalAmountCents = (sale: Sale | null | undefined): number =>
  sale?.totalAmountCurrencyInfo.amountCents ?? 0;

export const getSaleDiscountCents = (sale: Sale | null | undefined): number =>
  calculateAmountDiscountCents(
    getSaleSubtotalCents(sale),
    getSaleTotalAmountCents(sale),
    sale?.serviceFeeCurrencyInfo?.amountCents ?? 0,
  );

export const isSaleCouponApplied = (sale: Sale | null | undefined): boolean => Boolean(getSaleCoupon(sale)?.code);

export const getSaleCouponCode = (sale: Sale | null | undefined): string | undefined =>
  getSaleCoupon(sale)?.code ?? undefined;

export const getSaleCouponDiscountCents = (sale: Sale | null | undefined): number => {
  const coupon = getSaleCoupon(sale);

  if (!coupon) {
    return 0;
  }

  return getCouponDiscountCurrencyInfo(coupon).amountCents;
};

export const getSaleCouponPercentLabel = (sale: Sale | null | undefined): string | undefined =>
  getCouponPercentLabel(getSaleCoupon(sale), SaleCouponTypeEnum.Percentage);
